import React from 'react';
import { useParams } from 'react-router-dom';

import { EAdSenseProvider } from '@domain/enums/common/EAdSense';
import { ESynchronizationType } from '@domain/enums/hooks/ESynchronization';
import { IParams } from '@domain/interfaces/IParams';
import { IAdSenseAccount } from '@domain/interfaces/dashboard/AdSense/IAdSenseAccount';
import {
  ITikTokAdAccountsProviderProps,
  ITikTokAdAccountsProvider,
} from '@domain/interfaces/integrations/marketing/tiktok/ITikTokAdAccounts';

import { useToast } from '@helpers/hooks/useToast';
import { useTikTokCrendentials } from '@helpers/hooks/common/integrations/marketing/tiktok/useTikTokCrendentials';
import { useSynchronization } from '@helpers/hooks/common/useSynchronization';

import { ADS_PROVIDERS } from '@constants/common/integrations';

import adSenseAccountService from '@services/pages/dashboard/adSense/adSenseAccount';
import tikTokAccountService from '@services/pages/dashboard/adSense/tiktok/tiktokAccount';

const TikTokAdAccountsContext = React.createContext<ITikTokAdAccountsProvider | null>(null);

export const TikTokAdAccountsProvider: React.FC<ITikTokAdAccountsProviderProps> = ({
  adSenseCredentialAliasId,
  children,
}) => {
  const { storeAliasId } = useParams<IParams>();
  const { addSynchronization } = useSynchronization();
  const { toast } = useToast();
  const { adAccountSearchName } = useTikTokCrendentials();

  const [adSenseAccountsPage, setAdSenseAccountsPage] = React.useState<number>(0);
  const [adSenseAccountPageCount, setAdSenseAccountsPageCount] = React.useState<number>(1);
  const [adSenseAccounts, setAdSenseAccounts] = React.useState<Array<IAdSenseAccount>>([]);
  const [isLoadingAdSenseAccounts, setIsLoadingAdSenseAccounts] = React.useState<boolean>(false);
  const [isAdSenseAccountsError, setIsAdSenseAccountsError] = React.useState<boolean>(false);

  const loadAdSenseAccounts = React.useCallback(async () => {
    setIsLoadingAdSenseAccounts(true);
    setIsAdSenseAccountsError(false);

    try {
      const { data: adSenseAccountsData } = await adSenseAccountService.listAdSenseAccountsPromise({
        storeAliasId,
        adSenseCredentialAliasId,
        page: adSenseAccountsPage + 1,
        filter: {
          provider: EAdSenseProvider.TIKTOK,
          name: adAccountSearchName,
        },
      });

      if (adSenseAccountsData) {
        setAdSenseAccounts(currentAdSenseAccounts => [
          ...currentAdSenseAccounts,
          ...adSenseAccountsData.ad_sense_accounts,
        ]);
        setAdSenseAccountsPageCount(adSenseAccountsData.total_pages);
      }

      setIsLoadingAdSenseAccounts(false);
    } catch (error: any) {
      setIsLoadingAdSenseAccounts(false);
      setIsAdSenseAccountsError(true);
      toast.error(error?.response?.data?.message);
    }
  }, [adAccountSearchName, adSenseAccountsPage, storeAliasId, adSenseCredentialAliasId, toast]);

  const activateAdSenseAccount = React.useCallback(
    (adSenseAccountAliasId, data) => {
      const foundTikTokIntegration = ADS_PROVIDERS.find(
        integration => integration.identifier === EAdSenseProvider.TIKTOK,
      ) as typeof ADS_PROVIDERS[number];

      const { generateSyncRequest, name } = foundTikTokIntegration;

      const aliases = {
        adSenseAccountAliasId,
        adSenseCredentialAliasId,
      };

      addSynchronization({
        name,
        type: ESynchronizationType.UPDATE_AD_SENSE_ACCOUNTS,
        label: 'Perfil',
        dependencies: [],
        storeAliasId,
        externalId: adSenseAccountAliasId,
        config: {
          isOnboardingSynchronization: false,
          showNotification: true,
        },
        request: generateSyncRequest(
          ESynchronizationType.UPDATE_AD_SENSE_ACCOUNTS,
          storeAliasId,
          aliases,
          data,
        ),
      });
    },
    [adSenseCredentialAliasId, addSynchronization, storeAliasId],
  );

  const deactiveAdSenseAccount = React.useCallback(
    async (adSenseAccountAliasId, data) => {
      try {
        await tikTokAccountService.updateTiktokAccount({
          storeAliasId,
          adSenseAccountAliasId,
          adSenseCredentialAliasId: adSenseCredentialAliasId || '',
          data,
        });
      } catch (error: any) {
        toast.error(error?.response?.data?.message);
      }
    },
    [adSenseCredentialAliasId, storeAliasId, toast],
  );

  const resetInfiniteScroll = React.useCallback(() => {
    setAdSenseAccounts([]);
    setAdSenseAccountsPage(0);
  }, []);

  const updateAdSenseAccount = React.useCallback(
    async (adSenseAccountAliasId, data) => {
      if (data?.is_active) {
        activateAdSenseAccount(adSenseAccountAliasId, data);
      } else {
        await deactiveAdSenseAccount(adSenseAccountAliasId, data);
      }
    },
    [activateAdSenseAccount, deactiveAdSenseAccount],
  );

  const handleLoadMore = React.useCallback(() => {
    setAdSenseAccountsPage(oldPage => oldPage + 1);
  }, []);

  React.useEffect(() => {
    resetInfiniteScroll();
  }, [resetInfiniteScroll, adAccountSearchName]);

  const hasMore = adSenseAccountsPage + 1 < adSenseAccountPageCount;

  return (
    <TikTokAdAccountsContext.Provider
      value={{
        adSenseAccounts,
        handleLoadMore,
        hasMore,
        isAdSenseAccountsError,
        isLoadingAdSenseAccounts,
        loadAdSenseAccounts,
        updateAdSenseAccount,
        resetInfiniteScroll,
      }}
    >
      {children}
    </TikTokAdAccountsContext.Provider>
  );
};

export const useTikTokAdAccounts = (): ITikTokAdAccountsProvider => {
  const context = React.useContext(TikTokAdAccountsContext);

  if (!context) {
    throw new Error('useTikTokAdAccounts must be used within FacebookAdAccountsProvider');
  }

  return context;
};
